import { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, Spinner, X, UserPlus } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import CustomerLink from "@/components/CustomerLink";
import QuickCustomerDialog from "@/components/QuickCustomerDialog";
import { FavoriteButton, useFavorites } from "@/lib/useFavorites";
import { useAuth } from "@/lib/auth";
import { useCustomerDrawer } from "@/lib/useCustomerDrawer";

export default function Customers() {
  const { user } = useAuth();
  const { openCustomer } = useCustomerDrawer();
  const { favorites } = useFavorites();
  const canQuickCreate = user && ["Administrator", "Office Staff"].includes(user.role);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quickOpen, setQuickOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Debounce input for instant-but-not-spammy search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 180);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/customers", {
          params: debounced ? { q: debounced } : {},
        });
        if (!cancelled) setCustomers(res.data);
      } catch (e) {
        if (!cancelled) setError("Failed to load customers. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [debounced, reloadKey]);

  const stats = useMemo(() => {
    const active = customers.filter((c) => c.status === "Active").length;
    return { total: customers.length, active, inactive: customers.length - active };
  }, [customers]);

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        description="Search by customer name, account number, phone, or mailing address. Results update as you type. Customer records are read-only — synchronized weekly from the ERP system."
      />

      {/* Search bar */}
      <Card className="border-gray-200 p-4 shadow-none">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              data-testid="customer-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, account #, phone, or address…"
              className="h-11 pl-10 pr-10 text-base"
              autoFocus
            />
            {query && (
              <button
                data-testid="customer-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {loading ? (
              <>
                <Spinner size={14} className="animate-spin text-[#1A1A1A]" />
                <span>Searching…</span>
              </>
            ) : (
              <>
                <span data-testid="customer-results-count" className="font-mono font-semibold text-slate-700">
                  {stats.total}
                </span>
                <span>results</span>
                <span className="mx-1 text-slate-300">·</span>
                <span className="font-mono text-green-700">{stats.active}</span>
                <span>active</span>
              </>
            )}
          </div>
          {canQuickCreate && (
            <button
              data-testid="customers-quick-customer-btn"
              onClick={() => setQuickOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-brand-foreground hover:bg-brand-hover"
            >
              <UserPlus size={14} weight="bold" /> Quick Customer
            </button>
          )}
        </div>
      </Card>

      {favorites.length > 0 && !debounced && (
        <Card data-testid="customers-favorites-section" className="mt-5 border-slate-200 p-4 shadow-none">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Favorites
          </div>
          <div className="flex flex-wrap gap-2">
            {favorites.map((f) => (
              <button
                key={f.customer_id}
                data-testid={`customers-favorite-${f.customer_id}`}
                onClick={() => openCustomer(f.customer_id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
              >
                <span className="font-mono text-[10px] text-amber-600">#{f.account_number}</span>
                {f.customer_name}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Results table */}
      <Card className="mt-5 border-slate-200 shadow-sm">
        <div className="overflow-x-auto">
          <table data-testid="customers-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Account #</th>
                <th className="px-5 py-3 font-semibold">Customer Name</th>
                <th className="px-5 py-3 font-semibold">Address</th>
                <th className="px-5 py-3 font-semibold">City / Province</th>
                <th className="px-5 py-3 font-semibold">Postal</th>
                <th className="px-5 py-3 font-semibold">Primary Contact</th>
                <th className="px-5 py-3 font-semibold">Phone</th>
                <th className="px-5 py-3 font-semibold">Email</th>
                <th className="px-5 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {error && (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-sm text-red-600">
                    {error}
                  </td>
                </tr>
              )}
              {!error && !loading && customers.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-sm text-slate-500">
                    No customers match{" "}
                    <span className="font-mono text-slate-800">&ldquo;{debounced}&rdquo;</span>.
                  </td>
                </tr>
              )}
              {customers.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.account_number}`}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                >
                  <td className="px-5 py-3 font-mono text-slate-700">
                    <div className="flex items-center gap-1">
                      <FavoriteButton
                        customerId={c.id}
                        customerName={c.customer_name}
                        size={12}
                        testid={`customer-row-favorite-${c.id}`}
                      />
                      {c.account_number}
                      {c.source === "quick" && (
                        <span
                          data-testid={`customer-row-quick-${c.id}`}
                          className="ml-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700"
                          title="Quick customer — will be enriched on next ERP import"
                        >
                          Quick
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-medium text-slate-900">
                    <CustomerLink customerId={c.id} customerName={c.customer_name} testId={`customers-row-name-${c.id}`} />
                  </td>
                  <td className="px-5 py-3 text-slate-700">{c.mailing_address}</td>
                  <td className="px-5 py-3 text-slate-700">
                    {c.city}, {c.province}
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-700">{c.postal_code}</td>
                  <td className="px-5 py-3 text-slate-700">{c.primary_contact}</td>
                  <td className="px-5 py-3 font-mono text-slate-700">{c.phone_number}</td>
                  <td className="px-5 py-3 text-slate-700">
                    <a
                      href={`mailto:${c.email_address}`}
                      className="text-[#1A1A1A] hover:underline"
                    >
                      {c.email_address}
                    </a>
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <QuickCustomerDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        onCreated={() => setReloadKey((n) => n + 1)}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const styles =
    status === "Active"
      ? "bg-green-50 text-green-700 border-green-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      data-testid={`status-${status.toLowerCase()}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${styles}`}
    >
      {status}
    </span>
  );
}
